import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, Mail, Lock, User, Building2, Phone, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    org_name: "", org_slug: "", admin_name: "", admin_email: "",
    admin_password: "", phone: "",
  });

  const slugify = (v) => v.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/tenant/register", { ...form, org_slug: slugify(form.org_slug || form.org_name) });
      localStorage.setItem("f360_token", res.data.token);
      setUser(res.data.user);
      toast.success("Welcome to Fire360Pro!");
      nav("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center grid-bg px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-orange-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-pink-500/10 blur-3xl" />

      <div className="relative w-full max-w-2xl">
        <div className="glass-strong rounded-lg p-8 md:p-10 f360-border-gradient" data-testid="register-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-lg f360-gradient flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Flame className="w-6 h-6 text-gray-900" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold f360-gradient-text">Create your workspace</h1>
              <p className="text-xs text-gray-500">Start your 14-day free trial · Basic plan included</p>
            </div>
          </div>

          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-testid="register-form">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Organisation Name *</Label>
              <div className="relative mt-1">
                <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input required data-testid="reg-org-name" value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value, org_slug: form.org_slug || slugify(e.target.value) })} className="pl-10 bg-gray-50 border-gray-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Workspace URL Slug *</Label>
              <Input required data-testid="reg-slug" value={form.org_slug} onChange={(e) => setForm({ ...form, org_slug: slugify(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" placeholder="acme-corp" />
              <p className="text-[10px] text-gray-500 mt-1">fire360pro.com/login?org={form.org_slug || "your-slug"}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Admin Full Name *</Label>
              <div className="relative mt-1">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input required data-testid="reg-name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="pl-10 bg-gray-50 border-gray-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Admin Email *</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input required type="email" data-testid="reg-email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="pl-10 bg-gray-50 border-gray-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Mobile *</Label>
              <div className="relative mt-1">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input required type="tel" inputMode="tel" placeholder="98200 12345" data-testid="reg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="pl-10 bg-gray-50 border-gray-200" />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Password * (min 8 chars)</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input required type="password" minLength={8} data-testid="reg-password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="pl-10 bg-gray-50 border-gray-200" />
              </div>
            </div>

            <div className="md:col-span-2 flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
              <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
              <span>14-day free trial · 1 site · 5 users · No credit card</span>
            </div>

            <Button type="submit" disabled={busy} data-testid="reg-submit" className="md:col-span-2 h-12 f360-gradient text-white font-semibold border-0 btn-shimmer">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Create Workspace"}
            </Button>
          </form>

          <div className="text-center text-xs text-gray-500 mt-6">
            Already have an account? <Link to="/login" className="text-orange-400 hover:text-pink-400">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
